<script lang="ts" setup> 
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const { locale, locales } = useI18n();

// Store Flowbite instance for proper cleanup
let flowbiteInstance: any = null;
let exponeaNotificationModule: any = null;
// Track if component is mounted to prevent operations after unmount
const isMounted = ref(false);
// Debounce timer references for cleanup
const debounceTimers = ref<number[]>([]);

declare global {
  interface Window {
    $BV: any;
    bvCallback: (...args: any[]) => any;
    exponea: any;
    monetateQ: any;
    Trustpilot: any;
    gtag: any;
    OneTrust: any;
    NOIBUJS: any;
    flowbiteInstances: any;
    paypal: any;
    braintree: any;
  }
}

// Create a debounce utility to reduce CPU spikes from frequent function calls
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: number;
  return function(...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      fn.apply(null, args);
    }, ms);
    debounceTimers.value.push(timeoutId);
  };
};

useHead({
  link: [
    // Preconnect to CDN url
    {
      rel: 'preconnect',
      href: runtimeConfig.public.assetBaseUrl,
      crossorigin: 'anonymous',
    },
    // href language links
    ...generateHreflangLinks(
      route.fullPath,
      runtimeConfig.public.siteUrl,
      locale.value,
      locales.value
    ),
  ],
});

// Optimize async component loading with explicit loading and error handling
const TsSeoMetaTags = defineAsyncComponent({
  loader: () => import("~/components/TsSeoMetaTags.vue"),
  delay: 200,
  timeout: 5000,
  // Add error handling to prevent UI issues if component fails to load
  errorComponent: {
    template: '<!-- Error loading SEO meta tags -->'
  }
});

const TsModalEcomError = defineAsyncComponent({
  loader: () => import("~/components/TsModalEcomError.vue"),
  delay: 200,
  timeout: 5000,
  errorComponent: {
    template: '<!-- Error loading modal -->'
  }
});

const rootStore = useRootStore();

/**
 * Manually trigger exponea tracking and push notification script by calling `load()` when user consents tracking cookies
 */
const exponeaScriptSource =
    runtimeConfig.public.assetBaseUrl + "/static/exp.js";

const exponeaScript = useExponeaScript(exponeaScriptSource, {
  trigger: exponeaTrackingConsent,
  warmupStrategy: false
});

// Use a more efficient approach to handle script loading
if (exponeaScript) {
  exponeaScript.onLoaded(() => {
    if (!isMounted.value) return; // Prevent loading after component unmount

    // Use dynamic import with error handling
    import("~/services-optimized/exponea-notification-service")
        .then(({ checkAvailbility }) => {
          if (!isMounted.value) return; // Safety check
          checkAvailbility();
          exponeaNotificationModule = import("~/services-optimized/exponea-notification-service");
        })
        .catch(error => {
          console.error("Failed to load exponea notification service:", error);
        });
  });
}

// Memoize the checkout paths to avoid recreating the array on each function call
const CheckoutPaths = Object.freeze(["/trolley", "/checkout"]);

// Optimize trolley loading with proper error handling and memoization
const loadTrolley = debounce(async () => {
  if (!isMounted.value) return; // Safety check

  const onCheckout = CheckoutPaths.some((path) =>
      route.fullPath.includes(path)
  );

  /* Set the guest/customer trolley resources only when the header cart link is present in the DOM,
     This is to prevent trolley being loaded on "trolley"/"checkout"/"404" pages and reduce getProducts API calls */
  const headerCartLink = document.getElementById("header-cart-link");
  if (!headerCartLink || onCheckout) return;

  try {
    const { useTrolleyStore } = await import("~/stores/trolley.store");
    if (!isMounted.value) return; // Safety check after async operation

    const trolleyStore = useTrolleyStore();
    /* Set guest or customer trolley when app loads */
    trolleyStore.loadDefaultTrolley();
  } catch (error) {
    console.error("Failed to load trolley:", error);
  }
}, 300);

onMounted(async () => {
  isMounted.value = true;

  if (rootStore.debugPanel) {
    runtimeConfig.public.appDebug = true;
  }

  // Awaiting for dynamic store import
  loadTrolley();

  try {
    const { getBrowser } = await import("~/utils/dom.utils");
    if (!isMounted.value) return; // Safety check after async operation

    rootStore.browser = getBrowser();

    const { initFlowbite } = await import("flowbite");
    if (!isMounted.value) return; // Safety check after async operation

    // Use nextTick to ensure DOM is fully rendered before initializing Flowbite
    nextTick(() => {
      if (!isMounted.value) return; // Final safety check

      flowbiteInstance = initFlowbite();
      rootStore.isAppHydrated = true;
      // logStorageUsage(); // To view local and cookie storage consumption to monitor app health
    });
  } catch (error) {
    console.error("Error during app initialization:", error);
  }
});

// Comprehensive cleanup to prevent memory leaks
onBeforeUnmount(() => {
  isMounted.value = false;

  // Clear all debounce timers
  debounceTimers.value.forEach(timerId => {
    clearTimeout(timerId);
  });
  debounceTimers.value = [];

  // Clean up exponea script event listeners
  if (exponeaScript && typeof exponeaScript.cleanup === 'function') {
    try {
      exponeaScript.cleanup();
    } catch (error) {
      console.error("Error cleaning up exponea script:", error);
    }
  }

  // Clean up any Flowbite initializations
  if (flowbiteInstance) {
    try {
      flowbiteInstance.destroy();
    } catch (error) {
      console.error("Error destroying Flowbite instance:", error);
    }
    flowbiteInstance = null;
  }

  // Clean up exponea notification service module
  if (exponeaNotificationModule) {
    exponeaNotificationModule.then(module => {
      if (module && typeof module.cleanup === 'function') {
        try {
          module.cleanup();
        } catch (error) {
          console.error("Error cleaning up exponea notification module:", error);
        }
      }
    }).catch(() => {
      // Ignore errors during cleanup
    });
    exponeaNotificationModule = null;
  }
});

onUnmounted(() => {
  /* clear the refresh interval from plugin [refresh-access-token] */
  if (rootStore.accessTokenTimeoutId) clearTimeout(rootStore.accessTokenTimeoutId);
})
</script>

<template>
  <div>
    <DebugPanel v-if="rootStore.debugPanel" />
    <TsSeoMetaTags />
    <NuxtLoadingIndicator />
    <NuxtLayout>
      <TsModalEcomError v-if="rootStore.debugPanel" />
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
